import { Component, ChangeDetectorRef } from '@angular/core';
import { DxAspNetDataXsrfAwareService } from '@tools';
import DataSource from 'devextreme/data/data_source';
import { UrlEnhancerService } from '@url';
import { Config } from '@config';
import { LicenseService } from './license.service';
import { MessagesService, MessageType } from '@ui-messages';
import { Router } from '@angular/router';
import { __i18n } from '@localisation';
import { PrivilegesService } from '@privileges';
import { RoutePrivilegeNames } from '@route-privilege-names';

@Component({
    selector: 'license-list',
    templateUrl: './license-list.template.html',
    standalone: false
})
export class LicenseListComponent {

    constructor(
        private dxAspNetXsrfAwareService : DxAspNetDataXsrfAwareService,
        private urlEnhancer : UrlEnhancerService,
        private config : Config,
        private changeDetectorRef : ChangeDetectorRef,
        private service : LicenseService,
        private messageService : MessagesService,
        private router : Router,
        private privilegeService: PrivilegesService
    ){
        const privileges = this.privilegeService.getPrivileges();
        this.hasPrivSystemAdministrator = privileges.find(p => p.name === RoutePrivilegeNames.SystemAdministrator) !== undefined;
    }


    public remoteOperations = {
        paging: true, filtering: true, sorting: true, grouping: true, summary: true, groupPaging: true
    };

    public source: DataSource;

    public showLoader: boolean;

    private messageTarget : string = 'popup-messages';

    public hasPrivSystemAdministrator : boolean = false;

    public ngAfterViewInit(){
        let url = this.config.services.license.licenselist;
        url = this.urlEnhancer.replaceParams(url);

        this.source = new DataSource({
            paginate: true,
            store: this.dxAspNetXsrfAwareService.createStore({
                loadUrl: url,
                loadMethod: 'POST',
                key: 'plantFeatureLicenseId'
            })
        });

        this.changeDetectorRef.detectChanges();
    }

    public isPopUpVisible: boolean = false;

    public openAddLicensePopup(){
        this.isPopUpVisible = true;
        this.isActivationIdValid = false;
        this.newActivationId = '';
        this.newLicenseKey = '';
        this.messageService.deleteTargetMessages(this.messageTarget);
    }


    public newLicenseKey : string = '';

    public newActivationId : string = '';

    public addLicensButtonDisabled : boolean = null;

    private parseErrorMessageForAddActivationKey(e){

        if(e.status === 409){
            return __i18n('The given activation id is not a valid for the license management system.', 'license.list.activation-key-invalid.error.message')
        }
        if(e.status === 404){
            return __i18n('The given activation id was not found.', 'license.list.not-found-activation-key.error.message');
        }
        if(typeof e.error === 'string' &&  e.error.startsWith('{')) {
            e.error = JSON.parse(e.error);
        }

        if(typeof e.error === 'string') {
            return e.error;
        }

        if(e.error && e.error.error && e.error.error.errorCode === 0) {
            return __i18n('The request is invalid: Invalid activation key', 'license.list.invalid-activation-key.error.message')
        }

        if(e.error && e.error.error && e.error.error.errorText) {
            return e.error.error.errorText;
        }

        if(e.statusText){
            return e.statusText;
        }

        return 'Some error occured!'
    }

    private parseErrorMessageForAddLicenseId(e){

        if(e.status === 404){
            return __i18n('The given activation id was not found.', 'license.list.not-found-activation-key.error.message');
        }
        if(typeof e.error === 'string' &&  e.error.startsWith('{')) {
            e.error = JSON.parse(e.error);
        }

        if(typeof e.error === 'string') {
            return e.error;
        }

        if(e?.error?.error?.errorCode) {
            const errorText = this.service.getErrorTextFromCode(e.error.error.errorCode);
            if(errorText) {
                return errorText;
            }            
        }

        if(e?.error?.error?.errorText) {
            return e.error.error.errorText;
        }

        if(e.statusText){
            return e.statusText;
        }

        return 'Some error occured!'
    }

    private parseErrorMessageForValidateLicenseId(e){

        if(e.status === 409){
            return __i18n('The given activation id is not a valid for the license management system.', 'license.list.activation-key-invalid.error.message')
        }
        if(e.status === 404){
            return __i18n('The given activation id was not found.', 'license.list.not-found-activation-key.error.message');
        }
        if(typeof e.error === 'string' &&  e.error.startsWith('{')) {
            e.error = JSON.parse(e.error);
        }

        if(typeof e.error === 'string') {
            return e.error;
        }

        if(e.error && e.error.error && e.error.error.errorCode === 0) {
            return __i18n('The request is invalid: Invalid activation id', 'license.list.invalid-activation-id.error.message')
        }

        if(e.error && e.error.error && e.error.error.errorText) {
            return e.error.error.errorText;
        }

        if(e.statusText){
            return e.statusText;
        }

        return 'Some error occured!'
    }

    private wait(){
        return new Promise((resolve) => {
            setTimeout(resolve, 20000);
        })
    }

    public async addLicenseKey(){
        try {
            const value = (this.newLicenseKey) ? this.newLicenseKey.trim() : '';
            this.addLicensButtonDisabled = true;
            this.showLoader = true;
            if(this.newActivationId){
                const validation = await this.service.validateActivationId(this.newLicenseKey, this.newActivationId);
                if (validation.state !== 'ReadyForActivation') {
                    throw new Error('This activation id cannot be activated.');
                }
            }
            const result = await this.service.addNewActivationKey(value);
            this.messageService.addMessage({
                text : __i18n('Activation Key successfully created!', 'license.list.activation-key-created.message'),
                type: MessageType.success
            })
            if(this.newActivationId){
                try {
                    await this.service.addNewActivationId(this.newLicenseKey.trim(), this.newActivationId.trim());
                }catch(e){
                    this.messageService.addMessage({
                        text : this.parseErrorMessageForAddLicenseId(e),
                        type: MessageType.error
                    })
                }finally{
                    this.showLoader = false;
                }
                this.newActivationId = ' ';
            }
            this.isPopUpVisible = false;
            this.source.reload();
            this.newLicenseKey = ' ';
            this.isActivationIdValid = false;
        }catch(e){
            this.messageService.addMessage({
                text : this.parseErrorMessageForAddActivationKey(e),
                type: MessageType.error,
                target: this.messageTarget
            })
            console.error('e', e)
        }finally{
            this.showLoader = false;
            this.addLicensButtonDisabled = null;
        }
    }

    public isActivationIdValid: boolean = false;
    public async validateActivationId() {
        this.showLoader = true;
        this.isActivationIdValid = false;
        try {
            const value = (this.newActivationId) ? this.newActivationId.trim() : '';
            const response = await this.service.validateActivationId(this.newLicenseKey, value);
            if (response === null) {
                this.messageService.addMessage({
                    text: __i18n('This activation id does not contain any supported plant features.', 'license.details.message.nosupportedplantfeatures'),
                    type: MessageType.error,
                    target: this.messageTarget
                });
            } else if (response.state !== 'ReadyForActivation') {
                this.messageService.addMessage({
                    text: __i18n('This activation id cannot be activated.', 'license.details.message.cannotbeactivated'),
                    type: MessageType.error,
                    target: this.messageTarget
                });
            } else {
                this.messageService.addMessage({
                    target: this.messageTarget,
                    text: __i18n('This activation id contains the following features: ', 'license.details.message.containsfeature') + response.features.join(', '),
                    type: MessageType.success
                });
                this.isActivationIdValid = true;
            }
        } catch (e) {
            this.messageService.addMessage({
                text : this.parseErrorMessageForValidateLicenseId(e),
                type: MessageType.error,
                target: this.messageTarget
            })
            console.error('e', e)
        } finally {
            this.showLoader = false;
        }
    }

}